import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Cart drawer upsell.
 * Expands a size picker per bracelet, resolves the variant from the chosen options,
 * adds it with line item properties, then lets the theme re-render the drawer.
 */
class CartUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const toggle = target.closest('[data-upsell-toggle]');
    if (toggle) {
      this.#toggle(/** @type {HTMLElement} */ (toggle.closest('[data-upsell-item]')));
      return;
    }

    const add = target.closest('[data-upsell-add]');
    if (add) {
      this.#add(/** @type {HTMLElement} */ (add.closest('[data-upsell-item]')));
    }
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const item = /** @type {HTMLElement} */ (event.target).closest('[data-upsell-item]');
    if (item) this.#update(/** @type {HTMLElement} */ (item));
  };

  /** @param {HTMLElement} item */
  #toggle(item) {
    const willOpen = !item.classList.contains('is-open');

    // Only one picker open at a time
    for (const other of this.querySelectorAll('[data-upsell-item].is-open')) {
      if (other !== item) this.#setOpen(/** @type {HTMLElement} */ (other), false);
    }

    this.#setOpen(item, willOpen);

    if (willOpen) {
      this.#update(item);
      requestAnimationFrame(() => {
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    }
  }

  /** @param {HTMLElement} item @param {boolean} open */
  #setOpen(item, open) {
    item.classList.toggle('is-open', open);
    item.querySelector('[data-upsell-toggle]')?.setAttribute('aria-expanded', String(open));
    const panel = item.querySelector('[data-upsell-panel]');
    if (panel) panel.hidden = !open;
  }

  /**
   * Always read the variants from the card's own JSON (never cached on the element),
   * so a card re-used by the drawer refresh can't carry another product's variants.
   * @param {HTMLElement} item
   */
  #variants(item) {
    try {
      return JSON.parse(item.querySelector('[data-upsell-variants]')?.textContent || '[]');
    } catch {
      return [];
    }
  }

  /** @param {HTMLElement} item */
  #resolveVariant(item) {
    const selected = this.#selected(item);
    if (!selected.every(Boolean)) return null;
    return this.#variants(item).find((v) => v.options.every((opt, i) => opt === selected[i])) || null;
  }

  /** @param {HTMLElement} item @returns {(string|null)[]} */
  #selected(item) {
    return [...item.querySelectorAll('fieldset[data-option-index]')].map((fs) => {
      const checked = /** @type {HTMLInputElement|null} */ (fs.querySelector('input:checked'));
      return checked ? checked.value : null;
    });
  }

  /** @param {HTMLElement} item */
  #update(item) {
    const variants = this.#variants(item);
    const selected = this.#selected(item);
    const fieldsets = [...item.querySelectorAll('fieldset[data-option-index]')];

    // Show the chosen value next to each label, and grey out combinations that are sold out
    fieldsets.forEach((fs, index) => {
      const label = fs.querySelector('[data-chosen-label]');
      if (label) label.textContent = selected[index] ? selected[index].split(' | ')[0] : '';

      for (const input of /** @type {NodeListOf<HTMLInputElement>} */ (fs.querySelectorAll('input'))) {
        const test = [...selected];
        test[index] = input.value;
        const possible = variants.some((v) => v.available && v.options.every((opt, i) => test[i] == null || opt === test[i]));
        input.disabled = !possible;
      }
    });

    const variant = this.#resolveVariant(item);

    const button = /** @type {HTMLButtonElement} */ (item.querySelector('[data-upsell-add]'));
    const label = item.querySelector('[data-add-label]');
    const price = /** @type {HTMLElement} */ (item.querySelector('[data-add-price]'));

    if (!variant) {
      const missing = fieldsets.find((fs, i) => !selected[i]);
      const name = missing?.querySelector('legend span')?.textContent?.toLowerCase() || 'size';
      label.textContent = `Select your ${name}`;
      price.hidden = true;
      button.disabled = true;
    } else if (!variant.available) {
      label.textContent = 'Sold out';
      price.hidden = true;
      button.disabled = true;
    } else {
      label.textContent = 'Add to bag';
      price.querySelector('[data-add-sale]').textContent = variant.sale;
      price.querySelector('[data-add-compare]').textContent = variant.compare;
      price.hidden = false;
      button.disabled = false;
    }
  }

  /** @param {HTMLElement} item */
  async #add(item) {
    // Resolve at click time from this card's own selections and variant list
    const variant = this.#resolveVariant(item);
    if (!variant || !variant.available) return;
    const variantId = String(variant.id);

    const button = /** @type {HTMLButtonElement} */ (item.querySelector('[data-upsell-add]'));
    const error = /** @type {HTMLElement} */ (item.querySelector('[data-upsell-error]'));
    const forWhom = /** @type {HTMLInputElement|null} */ (item.querySelector('input[name$="-for"]:checked'));

    button.disabled = true;
    button.classList.add('is-loading');
    error.hidden = true;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const root = window.Shopify?.routes?.root || '/';
      const addResponse = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: Number(variantId),
              quantity: 1,
              properties: {
                For: forWhom ? forWhom.value : 'Myself',
                _upsell: 'cart-drawer',
              },
            },
          ],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await addResponse.json();

      if (!addResponse.ok || data.status) {
        throw new Error(data.description || data.message || 'Could not add this bracelet. Please try again.');
      }

      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cart-upsell',
          itemCount: 1,
          sections: data.sections,
          didError: false,
        },
      });
    } catch (err) {
      deferred.reject(err);
      this.dispatchEvent(new CartErrorEvent({ error: err.message, code: 'INVALID' }));
      error.textContent = err.message;
      error.hidden = false;
      button.disabled = false;
    } finally {
      button.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('cart-upsell')) {
  customElements.define('cart-upsell', CartUpsell);
}
